import { capitalizeFirstLetter, convertVietnameseToEnglish } from '@/helpers/string';
import useLocationOptions from '@/hooks/useLocationOptions';
import useOptions from '@/hooks/useOptions';
import { locationApiCaller } from '@/redux/query';
import filterRulesSlice, {
  IRuleParamFilterKeys,
  ISearchInPaginationKeys,
  paginationSelector,
  searchInPaginationSelector,
} from '@/redux/slice/filterRules.slice';
import { ChoiceList, IndexFilters, TextField, useSetIndexFiltersMode } from '@shopify/polaris';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const initInputSearch = {
  product: '',
  collection: '',
  vendor: '',
  country: '',
  state: '',
  zipcode: '',
};

interface IProps {
  onSearchChange: (value: string) => void;
  searchValue: string;
}

const FilterRule = ({ onSearchChange, searchValue }: IProps) => {
  const dispatch = useDispatch();
  const params = useSelector(paginationSelector);
  const searchInPagination = useSelector(searchInPaginationSelector);

  const [stateOptions, setStateOptions] = useState<{ label: string; value: string }[]>([]);
  const [isInitInputSearch, setIsInitInputSearch] = useState(true);
  const [inputSearch, setInputSearch] = useState<ISearchInPaginationKeys>(initInputSearch);

  const { data: stateRes } = locationApiCaller.useGetStatesQuery({ perPage: -1 });
  const { productOptions, collectionOptions, vendorOptions } = useOptions({
    products: {
      name: searchInPagination.product,
      perPage: 25,
    },
    collections: {
      name: searchInPagination.collection,
      perPage: 25,
    },
    vendors: true,
  });

  const { countryOptions, unionOptions } = useLocationOptions({
    countries: true,
    union: true,
  });

  const countryListOptions = useMemo(() => {
    if (unionOptions?.length) {
      const newUnionOptions = unionOptions.filter((option) => option.value !== '*');
      return [...newUnionOptions, ...countryOptions];
    }
    return countryOptions;
  }, [countryOptions, unionOptions]);

  useEffect(() => {
    if (isInitInputSearch) {
      setIsInitInputSearch(false);
      setInputSearch({
        product: searchInPagination.product || '',
        collection: searchInPagination.collection || '',
        vendor: searchInPagination.vendor || '',
        country: searchInPagination.country || '',
        state: searchInPagination.state || '',
        zipcode: params.zipcodes || '',
      });
    }
  }, [
    isInitInputSearch,
    params.zipcodes,
    searchInPagination.collection,
    searchInPagination.country,
    searchInPagination.product,
    searchInPagination.state,
    searchInPagination.vendor,
  ]);

  useEffect(() => {
    if (stateRes?.data?.result?.length) {
      const states = stateRes.data.result;
      setStateOptions(states.map((state) => ({ label: state.name, value: state.countryCode + '-' + state.isoCode })));
    }
  }, [stateRes]);

  const clearInputSearch = useCallback(() => {
    setInputSearch(initInputSearch);
    dispatch(filterRulesSlice.actions.resetSearchInPagination());
  }, [dispatch]);

  const handleUpdateParams = useCallback(
    (key: keyof IRuleParamFilterKeys) => (value: string[]) => {
      dispatch(filterRulesSlice.actions.updatePagination({ [key]: value.length ? value : undefined, page: 1 }));
    },
    [dispatch],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchZipcode = useCallback(
    debounce((value: string) => {
      dispatch(filterRulesSlice.actions.updatePagination({ zipcodes: value, page: 1 }));
    }, 300),
    [dispatch],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInPagination = useCallback(
    debounce((key: keyof ISearchInPaginationKeys, value: string) => {
      dispatch(filterRulesSlice.actions.updateSearchInPagination({ ...searchInPagination, [key]: value }));
    }, 300),
    [dispatch, searchInPagination],
  );

  const handleRemoveFilter = useCallback(
    (key: keyof IRuleParamFilterKeys) => () => {
      const keyInSearch = key.slice(0, key.length - 1);
      dispatch(filterRulesSlice.actions.updatePagination({ [key]: undefined, page: 1 }));
      dispatch(filterRulesSlice.actions.updateSearchInPagination({ ...searchInPagination, [keyInSearch]: '' }));
      setInputSearch((prev) => ({ ...prev, [keyInSearch]: '' }));
    },
    [dispatch, searchInPagination],
  );

  const handleFiltersClearAll = useCallback(() => {
    let newParams = { ...params };
    Object.keys(params).forEach((key) => {
      if (!['page', 'perPage', 'sort', 'order'].includes(key)) {
        newParams[key as keyof IRuleParamFilterKeys] = undefined;
        clearInputSearch();
      }
    });
    dispatch(filterRulesSlice.actions.updatePagination({ ...newParams, page: 1 }));
    dispatch(filterRulesSlice.actions.resetSearchInPagination());
    onSearchChange('');
  }, [clearInputSearch, dispatch, onSearchChange, params]);

  const handleUpdateInputSearch = useCallback(
    (key: keyof ISearchInPaginationKeys) => (value: string) => {
      setInputSearch((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const filters = useMemo(() => {
    return [
      {
        key: 'products',
        label: 'Products',
        filter: (
          <>
            <TextField
              autoComplete="off"
              label="Search by name"
              value={inputSearch.product}
              onChange={(value) => {
                handleSearchInPagination('product', value);
                handleUpdateInputSearch('product')(value);
              }}
              clearButton
              onClearButtonClick={() => {
                handleSearchInPagination('product', '');
                handleUpdateInputSearch('product')('');
              }}
            />
            <ChoiceList
              title="Products"
              titleHidden
              choices={productOptions || []}
              selected={params.products || []}
              onChange={handleUpdateParams('products')}
              allowMultiple
            />
          </>
        ),
        shortcut: true,
      },
      {
        key: 'collections',
        label: 'Collections',
        filter: (
          <>
            <TextField
              autoComplete="off"
              label="Search by collection"
              value={inputSearch.collection}
              onChange={(value) => {
                handleSearchInPagination('collection', value);
                handleUpdateInputSearch('collection')(value);
              }}
              clearButton
              onClearButtonClick={() => {
                handleSearchInPagination('collection', '');
                handleUpdateInputSearch('collection')('');
              }}
            />
            <ChoiceList
              title="Collections"
              titleHidden
              choices={collectionOptions || []}
              selected={params.collections || []}
              onChange={handleUpdateParams('collections')}
              allowMultiple
            />
          </>
        ),
        shortcut: true,
      },
      {
        key: 'vendors',
        label: 'Vendors',
        filter: (
          <>
            <TextField
              autoComplete="off"
              label="Search by vendor"
              value={inputSearch.vendor}
              onChange={(value) => {
                handleUpdateInputSearch('vendor')(value);
              }}
              clearButton
              onClearButtonClick={() => {
                handleUpdateInputSearch('vendor')('');
              }}
            />
            <ChoiceList
              title="Vendors"
              titleHidden
              choices={vendorOptions?.filter((option) =>
                convertVietnameseToEnglish(option?.label?.toLowerCase())?.includes(
                  convertVietnameseToEnglish(inputSearch.vendor?.toLowerCase() || ''),
                ),
              )}
              selected={params.vendors || []}
              onChange={handleUpdateParams('vendors')}
              allowMultiple
            />
          </>
        ),
        shortcut: true,
      },
      {
        key: 'countries',
        label: 'Countries',
        filter: (
          <>
            <TextField
              autoComplete="off"
              label="Search by country"
              value={inputSearch.country}
              onChange={(value) => {
                handleSearchInPagination('country', value);
                handleUpdateInputSearch('country')(value);
              }}
              clearButton
              onClearButtonClick={() => {
                handleSearchInPagination('country', '');
                handleUpdateInputSearch('country')('');
              }}
            />
            <ChoiceList
              title="Countries"
              titleHidden
              choices={
                searchInPagination.country
                  ? countryListOptions?.filter((option) =>
                      convertVietnameseToEnglish(option.label.toLocaleLowerCase())?.includes(
                        convertVietnameseToEnglish(searchInPagination.country?.toLocaleLowerCase() || ''),
                      ),
                    )
                  : countryListOptions
              }
              selected={params.countries || []}
              onChange={handleUpdateParams('countries')}
              allowMultiple
            />
          </>
        ),
        shortcut: true,
      },
      {
        key: 'zipcodes',
        label: 'Zipcodes',
        filter: (
          <TextField
            autoComplete="off"
            label="Search by zipcode"
            value={inputSearch.zipcode}
            onChange={(value) => {
              handleUpdateInputSearch('zipcode')(value);
              handleSearchZipcode(value);
            }}
            clearButton
            onClearButtonClick={() => {
              handleUpdateInputSearch('zipcode')('');
              handleSearchZipcode('');
            }}
          />
        ),
        shortcut: true,
      },
      {
        key: 'states',
        label: 'States',
        filter: (
          <>
            <TextField
              autoComplete="off"
              label="Search by state"
              value={inputSearch.state}
              onChange={(value) => {
                handleSearchInPagination('state', value);
                handleUpdateInputSearch('state')(value);
              }}
              clearButton
              onClearButtonClick={() => {
                handleSearchInPagination('state', '');
                handleUpdateInputSearch('state')('');
              }}
            />
            <ChoiceList
              title="States"
              titleHidden
              choices={stateOptions
                ?.filter((option) =>
                  convertVietnameseToEnglish(option?.label?.toLowerCase()).includes(
                    convertVietnameseToEnglish(searchInPagination.state?.toLowerCase() || ''),
                  ),
                )
                ?.slice(0, 25)}
              selected={params.states || []}
              onChange={handleUpdateParams('states')}
              allowMultiple
            />
          </>
        ),
        shortcut: true,
      },
    ];
  }, [
    inputSearch.product,
    inputSearch.collection,
    inputSearch.vendor,
    inputSearch.country,
    inputSearch.zipcode,
    inputSearch.state,
    productOptions,
    params.products,
    params.collections,
    params.vendors,
    params.countries,
    params.states,
    handleUpdateParams,
    collectionOptions,
    vendorOptions,
    searchInPagination.country,
    searchInPagination.state,
    countryListOptions,
    stateOptions,
    handleSearchInPagination,
    handleUpdateInputSearch,
    handleSearchZipcode,
  ]);

  const appliedFilters: {
    key: string;
    label: string;
    onRemove: () => void;
  }[] = useMemo(() => {
    const init: { key: string; label: string; onRemove: () => void }[] = [];
    Object.keys(params).forEach((key) => {
      if (params[key as keyof IRuleParamFilterKeys]) {
        init.push({
          key: key,
          label: capitalizeFirstLetter(key),
          onRemove: handleRemoveFilter(key as keyof IRuleParamFilterKeys),
        });
      }
    });
    return init;
  }, [params, handleRemoveFilter]);

  const { mode, setMode } = useSetIndexFiltersMode();

  return (
    <div>
      <IndexFilters
        cancelAction={{ onAction: handleFiltersClearAll }}
        queryValue={searchValue}
        queryPlaceholder="Search rule name"
        onQueryChange={onSearchChange}
        onQueryClear={() => onSearchChange('')}
        tabs={[]}
        selected={0}
        filters={filters}
        appliedFilters={appliedFilters}
        onClearAll={handleFiltersClearAll}
        mode={mode}
        setMode={setMode}
        canCreateNewView={false}
      />
    </div>
  );
};

export default FilterRule;
